// Funciones puras para elegir qué atributos de un inscrito aparecen en la
// etiqueta, listar los valores distintos que existen para cada uno, y
// resolver el texto que se debe mostrar (natural o remapeado por el admin).
// No tocan Firebase ni UI — mismo criterio que bulkEditLogic.js.

import {
  listarModalidades,
  resolverModalidadLabel,
  resolverCategoriaLabel,
} from "../Configuration/BulkEdit/bulkEditLogic";

const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };

// nombres/apellidos/número ya tienen una posición fija en la etiqueta y no
// tiene sentido remapear sus valores (son texto libre por persona, no un
// catálogo cerrado) — no pasan por este selector.
const CAMPOS_FIJOS_SELECCIONABLES = [
  { key: "modalidad", label: "Modalidad" },
  { key: "rama", label: "Género" },
  { key: "categoria", label: "Categoría" },
];

// Combina los campos fijos con los campos personalizados definidos en
// registryConfiguration.gruposFormularios — la copia raíz que CreateInput.jsx
// escribe SIEMPRE al crear un campo, sea cual sea su grupo (confirmado
// leyendo su onSubmit), así que es un superset completo sin necesidad de
// recorrer también las copias por modalidad.
export function listarAtributosEtiquetables(registryConfiguration) {
  const fijos = CAMPOS_FIJOS_SELECCIONABLES.map((c) => ({ ...c, source: "core" }));

  const grupos = (registryConfiguration && registryConfiguration.gruposFormularios) || {};
  const vistos = new Set();
  const personalizados = [];
  Object.values(grupos).forEach((grupo) => {
    Object.values(grupo || {}).forEach((campo) => {
      if (!campo || !campo.iDCampo || vistos.has(campo.iDCampo)) return;
      vistos.add(campo.iDCampo);
      personalizados.push({
        key: campo.iDCampo,
        label: campo.etiqueta || campo.iDCampo,
        source: "custom",
        tipoCampo: campo.tipoCampo,
        opcionesCanonicas:
          campo.tipoCampo === "Select" && Array.isArray(campo.valor) ? campo.valor : null,
      });
    });
  });

  return [...fijos, ...personalizados];
}

// Valores distintos disponibles para un atributo, listos para el editor de
// remapeo — [{ raw, label }]. Para modalidad/categoría/género se apoya en
// los helpers ya existentes de bulkEditLogic.js (no se reinventan); para
// campos personalizados tipo Select se usan sus opciones canónicas
// (filtrando las marcadas oculto:true) más cualquier valor suelto que
// aparezca en los participantes pero no esté en el catálogo; para el resto
// se escanean los valores presentes en los participantes filtrados.
export function listarValoresDistintos(
  attributeKey,
  attributeMeta,
  participantesFiltrados,
  eventSubEventsRegistry
) {
  const participantes = participantesFiltrados || [];

  if (attributeKey === "modalidad") {
    return listarModalidades(eventSubEventsRegistry).map((m) => ({
      raw: m.value,
      label: m.label,
    }));
  }
  if (attributeKey === "rama") {
    return Object.keys(NOMBRE_RAMA).map((r) => ({ raw: r, label: NOMBRE_RAMA[r] }));
  }
  if (attributeKey === "categoria") {
    // Las categorías dependen de la modalidad, así que no hay un catálogo
    // único — se listan las combinaciones realmente presentes en los
    // participantes filtrados, con su etiqueta ya resuelta.
    const vistos = new Map();
    participantes.forEach((p) => {
      if (p.categoria === undefined || p.categoria === null || p.categoria === "") return;
      const raw = String(p.categoria);
      if (vistos.has(raw)) return;
      vistos.set(
        raw,
        resolverCategoriaLabel(eventSubEventsRegistry, p.modalidad, p.rama, p.categoria)
      );
    });
    return Array.from(vistos.entries()).map(([raw, label]) => ({ raw, label }));
  }

  if (attributeMeta && attributeMeta.opcionesCanonicas) {
    const visibles = attributeMeta.opcionesCanonicas.filter((o) => o.oculto !== true);
    const canonicos = visibles.map((o) => ({ raw: o.valor, label: o.texto }));
    const rawCanonicos = new Set(canonicos.map((c) => c.raw));
    const sueltos = new Set();
    participantes.forEach((p) => {
      const v = p[attributeKey];
      if (v === undefined || v === null || v === "") return;
      if (!rawCanonicos.has(v)) sueltos.add(String(v));
    });
    return canonicos.concat(Array.from(sueltos).map((raw) => ({ raw, label: raw })));
  }

  // Campo libre (texto, etc.): se escanean los valores presentes.
  const vistos = new Set();
  participantes.forEach((p) => {
    const v = p[attributeKey];
    if (v === undefined || v === null || v === "") return;
    vistos.add(String(v));
  });
  return Array.from(vistos)
    .sort((a, b) => a.localeCompare(b))
    .map((raw) => ({ raw, label: raw }));
}

// Texto "natural" (sin remapeo) de un atributo para un participante dado.
export function resolverEtiquetaNatural(attributeKey, participante, attributeMeta, eventSubEventsRegistry) {
  if (!participante) return "";
  if (attributeKey === "modalidad") {
    return resolverModalidadLabel(eventSubEventsRegistry, participante.modalidad);
  }
  if (attributeKey === "rama") {
    return NOMBRE_RAMA[participante.rama] || participante.rama || "";
  }
  if (attributeKey === "categoria") {
    return resolverCategoriaLabel(
      eventSubEventsRegistry,
      participante.modalidad,
      participante.rama,
      participante.categoria
    );
  }
  const raw = participante[attributeKey];
  if (attributeMeta && attributeMeta.opcionesCanonicas) {
    const match = attributeMeta.opcionesCanonicas.find((o) => o.valor === raw);
    if (match) return match.texto;
  }
  return raw === undefined || raw === null ? "" : String(raw);
}

// Texto final a mostrar en la etiqueta: el override del admin (valueRemap)
// si existe para el valor crudo de este participante, o si no, el texto
// natural.
export function resolverDisplayValue(
  attributeKey,
  participante,
  attributeMeta,
  eventSubEventsRegistry,
  valueRemap
) {
  if (!participante) return "";
  const raw = participante[attributeKey];
  const remap = valueRemap && valueRemap[attributeKey];
  const override = remap && remap[String(raw)];
  if (override && override.display) return override.display;
  return resolverEtiquetaNatural(attributeKey, participante, attributeMeta, eventSubEventsRegistry);
}

// Reemplaza variables entre corchetes dentro de un texto libre — misma
// sintaxis que ya usa el editor de bloques de correo (ver
// functions/modules/mailingfunctions.js: replaceVars), para poder
// concatenar texto fijo con datos del participante en una sola celda, ej.
// "[modalidad] - [categoria]". `resolverValor(key)` decide qué devolver
// para cada clave encontrada.
const REGEX_VARIABLE = /\[\s*([\w$][\w$.]*)\s*\]/g;

export function resolverTextoConVariables(template, resolverValor) {
  if (!template) return "";
  return String(template).replace(REGEX_VARIABLE, (match, key) => {
    const valor = resolverValor(key);
    return valor === undefined || valor === null ? "" : String(valor);
  });
}

// Atajo para resolver UNA clave (usado tanto directo como dentro de
// resolverTextoConVariables): "nombreCompleto" es la única clave sintética
// que queda (nombre completo en mayúsculas, como se veía en el diseño
// original) — todo lo demás (modalidad, categoría, género, número, campos
// personalizados) se resuelve igual que un atributo normal.
export function resolverValorAtributo(key, participante, atributosPorKey, eventSubEventsRegistry, valueRemap) {
  if (!participante) return "";
  if (key === "nombreCompleto") {
    return `${(participante.nombres || "").trim().toUpperCase()} ${(participante.apellidos || "")
      .trim()
      .toUpperCase()}`;
  }
  return resolverDisplayValue(
    key,
    participante,
    atributosPorKey && atributosPorKey[key],
    eventSubEventsRegistry,
    valueRemap
  );
}
